import React from 'react';
import {
  Button,
  Icon,
  Text,
  useDisclosure,
  Tooltip,
} from '@wegroup/design-system';
import useTracking from '../../../../../../../common/src/hooks/tracking/useTracking';
import usePartyId from '../../../../../../../common/src/hooks/query-params/usePartyId';
import ShareCompanyScanConversationModal from '../../../../../../../risk-analysis/src/pages/recommendations/ShareCompanyScanConversationModal';
import { useLocale } from '../../../../../../../../i18n';
import { useAskLaterQuestions } from '../ask-later-questions/api/getAskLaterQuestion';
import { useParty } from '../../../../../../../parties/src/features/detail/api/getParty';
import { useTranslation } from 'react-i18next';
import { useFlowsRouteParams } from '../../../../../hooks/useFlowsRouteParams';
import { useFormState } from 'react-hook-form';
import { useFlowsProgression } from '../../../../../hooks/useFlowsProgression';
import useToggleCompanyOverview from '../../hooks/useToggleCompanyOverview';
import { useFlowsSelector } from '../../../../../../../common/src/utils/globalVariables';

interface Props {
  iconSize?: string;
}

const BottomBarNextButton: React.FC<React.PropsWithChildren<Props>> = ({
  iconSize,
}) => {
  const { t } = useTranslation();
  const { position } = useFlowsRouteParams();
  const { isSubmitting } = useFormState();
  const { nextQuestion } = useFlowsProgression();
  const { isOverview } = useToggleCompanyOverview();
  const { trackEvent } = useTracking();
  const isViewOnlyMode = useFlowsSelector(
    (state) => state.flows.isViewOnlyModeOn,
  );

  const { isOpen, onOpen, onClose } = useDisclosure();
  const partyId = usePartyId();
  const { data: party, isLoading } = useParty(partyId);
  const { userLang } = useLocale();
  const { data: askLater, isLoading: isLoadingAskLater } =
    useAskLaterQuestions();

  // Redux
  const lastPosition = useFlowsSelector(
    (state) => state.flowsMetaData.lastPosition,
  );
  const groups = useFlowsSelector((state) => state.flows.questionnaire.groups);

  const isLast = position
    ? parseInt(position) === groups.length - 1
    : lastPosition === groups.length - 1;

  const onNextClick = (e: React.MouseEvent) => {
    if (isLast) {
      trackEvent('Get Recommendation in Company Scan through Next Button');
    }
    e.stopPropagation();
    nextQuestion();
  };

  const handleShare = () => {
    trackEvent('Clicks on share conversation at the end of the company scan');
    onOpen();
  };

  if (isLast && isViewOnlyMode)
    return (
      <>
        {party && (
          <ShareCompanyScanConversationModal
            isOpen={isOpen}
            onClose={onClose}
            partyLang={party.native_language || userLang}
            partyId={party.id}
          />
        )}
        <Tooltip
          label={t('SHARE-CONVERSATION_TOOLTIP_DISABLED')}
          isDisabled={(askLater?.questions.length || 0) > 0}
          shouldWrapChildren
          placement="left"
        >
          <Button
            borderRadius="full"
            onClick={handleShare}
            leftIcon={<Icon name="GeneralActionsLink" />}
            isDisabled={askLater?.questions.length === 0}
            isLoading={isLoading || isLoadingAskLater}
          >
            {t('SHARE-CONVERSATION')}
          </Button>
        </Tooltip>
      </>
    );

  return (
    <Button
      data-test-id="COMPANY-SCAN_BOTTOM-BAR_ACTION-BUTTON_NEXT"
      minW={{ base: '12', tablet: '40' }}
      onClick={onNextClick}
      borderRadius="full"
      rightIcon={
        isLast ? (
          <Icon name="GeneralStatusSuccessCheckmark" boxSize={iconSize} />
        ) : (
          <Icon name="GeneralActionsArrowRight" boxSize={iconSize} />
        )
      }
      pl={{ base: '6', laptopL: '10' }}
      isLoading={isSubmitting}
      isDisabled={isSubmitting || isOverview}
    >
      <Text display={{ base: 'none', tabletS: 'block' }}>
        {isLast ? t('FINISH') : t('NEXT')}
      </Text>
    </Button>
  );
};

export default BottomBarNextButton;
